import { AbstractControl, FormGroup } from '@angular/forms';

/**
 * Automatically enables or disables target controls in a FormGroup
 * based on the value of a source control, with optional cross-field validation.
 *
 * For each declared pair:
 * - If the source control has a truthy value, the target control is enabled.
 * - Otherwise, the target control is reset to `null` and disabled.
 *
 * If a `rule` is provided in the options:
 * - It is evaluated only when both source and target values are present.
 * - If the rule fails, the `errorType` error is set on the target control.
 * - If the rule passes, errors on the target control are cleared.
 *
 * No change events are emitted during internal modifications
 * (enable/disable/reset/setErrors) to prevent change detection loops.
 *
 * @example
 * formWithCrossFieldsValidation(form, [
 *   [
 *     'nationality',
 *     'registerNumber',
 *     {
 *       rule: (nationality, registerNumber) =>
 *         nationality?.value === Nationality.BE && PATTERN.test(registerNumber),
 *       errorType: 'registerNumber',
 *     },
 *   ],
 * ]);
 *
 * @param formGroup The FormGroup containing the controls to manage.
 * @param controlsPairPaths Array of tuples [sourceKey, targetKey, options?].
 *   - `sourceKey`: key of the source control (determines whether the target is enabled).
 *   - `targetKey`: key of the target control (enabled/disabled/validated).
 *   - `options.rule`: cross-field validation function (sourceValue, targetValue) → boolean.
 *   - `options.errorType`: error key set on the target control if the rule fails.
 *
 * @returns The same FormGroup instance (for chaining).
 *
 * @throws Error if the provided control is not an instance of `FormGroup`.
 * @throws Error if none of the declared pairs match existing controls in the FormGroup.
 */
export function formWithCrossFieldsValidation<T extends AbstractControl>(
  formGroup: T,
  controlsPairPaths: [
    keyof T['value'],
    keyof T['value'],
    options?: { rule: (sourceValue: any, targetValue: any) => boolean; errorType: string },
  ][]
): T {
  const someControlsDontExistInFormGroup = !controlsPairPaths.some(
    (controlPair) => !!formGroup.get(controlPair[0] as string) && !!formGroup.get(controlPair[1] as string)
  );

  if (!(formGroup instanceof FormGroup)) {
    throw new Error('The provided control is not an instance of `FormGroup`');
  } else if (someControlsDontExistInFormGroup) {
    throw new Error('One of the controls pair does not exist in the form group provided.');
  }

  const applyPair = (
    pair: (typeof controlsPairPaths)[number],
    rawValue: T['value']
  ): void => {
    const [sourceControlKey, targetControlKey, options] = pair;

    const sourceRawValue = rawValue[sourceControlKey];
    const targetRawValue = rawValue[targetControlKey];

    const canEnableControl =
      !!sourceRawValue ||
      (typeof sourceRawValue === 'boolean' && sourceRawValue === true);

    if (canEnableControl) {
      formGroup.get(targetControlKey as string)?.enable({ emitEvent: false });
    } else {
      formGroup.get(targetControlKey as string)?.reset(null, { emitEvent: false });
      formGroup.get(targetControlKey as string)?.disable({ emitEvent: false });
    }

    if (!sourceRawValue || !targetRawValue) {
      formGroup.get(targetControlKey as string)?.setErrors(null, { emitEvent: false });
      return;
    }

    if (!options?.rule) return;

    if (!options.rule(sourceRawValue, targetRawValue)) {
      formGroup
        .get(targetControlKey as string)
        ?.setErrors({ [options.errorType]: true }, { emitEvent: false });
    } else {
      formGroup.get(targetControlKey as string)?.setErrors(null, { emitEvent: false });
    }
  };

  // Apply initial state immediately, before any value change occurs
  controlsPairPaths.forEach((pair) => applyPair(pair, formGroup.getRawValue()));

  formGroup.valueChanges.subscribe(() => {
    const rawValue = formGroup.getRawValue();
    controlsPairPaths.forEach((pair) => applyPair(pair, rawValue));
  });

  return formGroup;
}
